// src/components/child/OffStreetPolygonEditShimmer.jsx
import { useEffect, useRef } from 'react';

// ─── Inject keyframes once ────────────────────────────────────────────────────
const STYLE_ID = 'off-street-polygon-edit-shimmer-keyframes';
const injectStyles = () => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes offStreetPolygonEditShimmerSlide {
      0%   { background-position: -700px 0; }
      100% { background-position:  700px 0; }
    }
    @keyframes offStreetPolygonEditShimmerFadeOut {
      0%   { opacity: 1; }
      100% { opacity: 0; }
    }
    @keyframes offStreetPolygonEditShimmerFadeIn {
      0%   { opacity: 0; }
      100% { opacity: 1; }
    }
  `;
  document.head.appendChild(style);
};

// ─── Single shimmer block ─────────────────────────────────────────────────────
const ShimmerBlock = ({ style = {} }) => (
  <div
    style={{
      background:
        'linear-gradient(90deg, var(--shimmer-base) 25%, var(--shimmer-highlight) 50%, var(--shimmer-base) 75%)',
      backgroundSize: '700px 100%',
      animation: 'offStreetPolygonEditShimmerSlide 1.5s ease-in-out infinite',
      borderRadius: 8,
      ...style,
    }}
  />
);

// ─── Mode configs ─────────────────────────────────────────────────────────────
const MODE_CONFIG = {
  overview: {
    blobs: [
      { top: '22%', left: '16%', w: 110, h: 36 },
      { top: '38%', left: '28%', w: 88,  h: 28 },
      { top: '54%', left: '48%', w: 104, h: 32 },
      { top: '30%', left: '60%', w: 76,  h: 26 },
      { top: '62%', left: '20%', w: 94,  h: 30 },
      { top: '68%', left: '62%', w: 80,  h: 26 },
    ],
    roads: [
      { top: '44%', left: '10%', w: '35%' },
      { top: '58%', left: '38%', w: '28%' },
      { top: '70%', left: '55%', w: '22%' },
    ],
    pins: [
      { top: '50%', left: '22%' },
      { top: '36%', left: '44%' },
      { top: '28%', left: '65%' },
      { top: '58%', left: '37%' },
      { top: '65%', left: '58%' },
    ],
  },
  city: {
    blobs: [
      { top: '25%', left: '20%', w: 96,  h: 30 },
      { top: '35%', left: '38%', w: 80,  h: 26 },
      { top: '50%', left: '52%', w: 92,  h: 28 },
      { top: '60%', left: '25%', w: 74,  h: 24 },
      { top: '42%', left: '64%', w: 86,  h: 28 },
    ],
    roads: [
      { top: '40%', left: '15%', w: '32%' },
      { top: '55%', left: '40%', w: '25%' },
      { top: '65%', left: '60%', w: '20%' },
    ],
    pins: [
      { top: '48%', left: '24%' },
      { top: '33%', left: '46%' },
      { top: '58%', left: '40%' },
      { top: '27%', left: '67%' },
      { top: '68%', left: '55%' },
      { top: '40%', left: '58%' },
    ],
  },
  street: {
    blobs: [
      { top: '38%', left: '10%', w: 70, h: 24 },
      { top: '38%', left: '28%', w: 70, h: 24 },
      { top: '38%', left: '46%', w: 70, h: 24 },
      { top: '38%', left: '64%', w: 70, h: 24 },
    ],
    roads: [
      { top: '48%', left: '8%', w: '84%' },
      { top: '52%', left: '8%', w: '84%' },
    ],
    pins: [
      { top: '32%', left: '14%' },
      { top: '32%', left: '32%' },
      { top: '32%', left: '50%' },
      { top: '32%', left: '68%' },
      { top: '58%', left: '23%' },
      { top: '58%', left: '41%' },
      { top: '58%', left: '59%' },
    ],
  },
  pnd: {
    blobs: [
      { top: '30%', left: '35%', w: 120, h: 34 },
      { top: '58%', left: '30%', w: 140, h: 28 },
      { top: '66%', left: '45%', w: 100, h: 22 },
    ],
    roads: [
      { top: '50%', left: '20%', w: '60%' },
      { top: '54%', left: '20%', w: '60%' },
    ],
    pins: [
      { top: '36%', left: '47%' },
    ],
  },
};

// ─── OffStreetPolygonEditShimmer ──────────────────────────────────────────────
/**
 * Props
 * ─────
 * visible      {boolean}  — show/hide the overlay
 * onHidden     {function} — called after fade-out animation completes
 * isDarkMode   {boolean}  — dark or light palette
 * mode         {string}   — 'overview' | 'city' | 'street' | 'pnd'
 */
const OffStreetPolygonEditShimmer = ({ visible = true, onHidden, isDarkMode = true, mode = 'overview' }) => {
  const overlayRef  = useRef(null);
  const prevVisible = useRef(null);

  useEffect(() => {
    injectStyles();
  }, []);

  // handle fade-in / fade-out
  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;

    if (prevVisible.current === null) {
      // first render — no animation
      el.style.opacity       = visible ? '1' : '0';
      el.style.pointerEvents = visible ? 'auto' : 'none';
      prevVisible.current    = visible;
      return;
    }

    if (visible && !prevVisible.current) {
      el.style.pointerEvents = 'auto';
      el.style.opacity       = '0';
      el.style.animation     = 'offStreetPolygonEditShimmerFadeIn 0.25s ease-out forwards';
    } else if (!visible && prevVisible.current) {
      el.style.animation = 'offStreetPolygonEditShimmerFadeOut 0.45s ease-out forwards';
      const timer = setTimeout(() => {
        if (overlayRef.current) overlayRef.current.style.pointerEvents = 'none';
        onHidden?.();
      }, 460);
      prevVisible.current = visible;
      return () => clearTimeout(timer);
    }

    prevVisible.current = visible;
  }, [visible, onHidden]);

  const cssVars = isDarkMode
    ? {
        '--shimmer-base':          'rgba(255,255,255,0.06)',
        '--shimmer-highlight':     'rgba(255,255,255,0.14)',
        '--shimmer-bg':            'rgba(20, 22, 32, 0.82)',
        '--shimmer-dot':           'rgba(99, 102, 241, 0.55)',
        '--shimmer-dot-selected':  'rgba(99, 102, 241, 1)',
      }
    : {
        '--shimmer-base':          'rgba(0,0,0,0.07)',
        '--shimmer-highlight':     'rgba(0,0,0,0.03)',
        '--shimmer-bg':            'rgba(240, 242, 248, 0.85)',
        '--shimmer-dot':           'rgba(99, 102, 241, 0.45)',
        '--shimmer-dot-selected':  'rgba(99, 102, 241, 0.9)',
      };

  const cfg = MODE_CONFIG[mode] || MODE_CONFIG.overview;

  return (
    <div
      ref={overlayRef}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 10,
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        backgroundColor: cssVars['--shimmer-bg'],
        overflow: 'hidden',
        ...cssVars,
      }}
    >
      {/* ── Label blobs ── */}
      {cfg.blobs.map((b, i) => (
        <ShimmerBlock
          key={`blob_${i}`}
          style={{ position: 'absolute', top: b.top, left: b.left, width: b.w, height: b.h }}
        />
      ))}

      {/* ── Road lines ── */}
      {cfg.roads.map((r, i) => (
        <ShimmerBlock
          key={`road_${i}`}
          style={{
            position: 'absolute', top: r.top, left: r.left,
            width: r.w, height: 5, borderRadius: 3, opacity: 0.45,
          }}
        />
      ))}

      {/* ── Pin markers ── */}
      {cfg.pins.map((pos, i) => {
        const isSelected = mode === 'pnd' && i === 0;
        return (
          <div key={`pin_${i}`} style={{ position: 'absolute', top: pos.top, left: pos.left }}>
            <div style={{
              width:        isSelected ? 22 : 14,
              height:       isSelected ? 22 : 14,
              borderRadius: '50%',
              background:   isSelected ? cssVars['--shimmer-dot-selected'] : cssVars['--shimmer-dot'],
              marginBottom: 2,
              boxShadow:    isSelected ? `0 0 0 6px ${cssVars['--shimmer-dot']}` : 'none',
            }} />
            <div style={{
              width:        isSelected ? 5 : 3,
              height:       isSelected ? 10 : 7,
              borderRadius: '0 0 3px 3px',
              background:   isSelected ? cssVars['--shimmer-dot-selected'] : cssVars['--shimmer-dot'],
              margin:       '0 auto',
              opacity:      0.6,
            }} />
          </div>
        );
      })}

      {/* ── Map control skeletons ── */}
      <ShimmerBlock style={{ position: 'absolute', bottom: 20, right: 16, width: 32, height: 32, borderRadius: 6 }} />
      <ShimmerBlock style={{ position: 'absolute', bottom: 60, right: 16, width: 32, height: 32, borderRadius: 6 }} />
      <ShimmerBlock style={{ position: 'absolute', top:    16, right: 16, width: 32, height: 32, borderRadius: 6 }} />
    </div>
  );
};

export default OffStreetPolygonEditShimmer;